/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogProps,
  DialogTitle,
} from '@eigtech/ui/components/dialog'
import { FieldValues, SubmitHandler } from 'react-hook-form'
import { FormApi } from '../useForm'

export type FormModalProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
> = {
  children?: React.ReactNode
  dialogContentClassName?: string
  description?: React.ReactNode
  form: FormApi<TFieldValues, TContext, TTransformedValues>
  resetFormOnClose?: boolean
  footer?: React.ReactNode
  submitButton?: React.ReactNode
  title: React.ReactNode
  onSubmit: SubmitHandler<TTransformedValues> | undefined
} & DialogProps

export function FormModal<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
>({
  children,
  dialogContentClassName,
  description,
  form,
  resetFormOnClose = true,
  footer,
  submitButton,
  title,
  onSubmit,
  ...props
}: FormModalProps<TFieldValues, TContext, TTransformedValues>) {
  const {
    formState: { isSubmitting },
    reset,
  } = form

  return (
    <Dialog
      closeDisabled={isSubmitting}
      disablePointerDismissal
      {...props}
      onOpenChange={(open, event) => {
        if (resetFormOnClose && !open) {
          reset()
        }

        props.onOpenChange?.(open, event)
      }}
    >
      <DialogContent className={dialogContentClassName}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {!!description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <DialogBody>
          {onSubmit ? (
            <form.Form
              className="flex flex-1 flex-col gap-4 overflow-auto px-0.5 py-px"
              onSubmit={onSubmit}
            >
              {children}
            </form.Form>
          ) : (
            children
          )}
        </DialogBody>

        {footer || (
          <DialogFooter>
            <DialogClose>Cancel</DialogClose>

            {!submitButton || typeof submitButton === 'string' ? (
              <form.SubmitButton size="md">{submitButton}</form.SubmitButton>
            ) : (
              submitButton
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
